import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, QrCode, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { DRIVER_STATUS_LABELS, DRIVER_STATUS_VARIANT, fmtDay, licenseAlert } from '@/lib/drivers';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DriverForm, toDriverPayload, type DriverFormData } from '@/components/drivers/DriverForm';

import type { Driver, DriverStatus, MissionList } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/drivers/$id')({ component: DriverDetailPage });

interface OnboardingToken { qrDataUrl: string; url: string; expiresAt: string }

function DriverDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [onboarding, setOnboarding] = useState<OnboardingToken | null>(null);
  // fleet-service : modification, statut et QR d'activation exigent vehicles.write
  const canWrite = useHasPermission('vehicles.write');

  const { data: driver, isLoading } = useQuery({
    queryKey: ['driver', id],
    queryFn: async () => (await api.get<Driver>(`/drivers/${id}`)).data,
  });

  const { data: missions } = useQuery({
    queryKey: ['missions', { driverId: id }],
    queryFn: async () => (await api.get<MissionList>(`/missions?driverId=${id}&limit=50`)).data,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['driver', id] });
    void qc.invalidateQueries({ queryKey: ['drivers'] });
  };

  const update = useMutation({
    mutationFn: (data: DriverFormData) => api.patch(`/drivers/${id}`, toDriverPayload(data, null)),
    onSuccess: () => {
      invalidate();
      toast.success('Conducteur modifié');
      setEditing(false);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la modification')),
  });

  const setStatus = useMutation({
    mutationFn: (status: DriverStatus) => api.patch(`/drivers/${id}`, { status }),
    onSuccess: (_, status) => {
      invalidate();
      toast.success(`Statut : ${DRIVER_STATUS_LABELS[status]}`);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors du changement de statut')),
  });

  const genOnboarding = useMutation({
    mutationFn: async () => (await api.post<OnboardingToken>(`/drivers/${id}/onboarding`)).data,
    onSuccess: setOnboarding,
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la génération du QR code')),
  });

  if (isLoading) return <div className="p-6"><Skeleton className="h-48 w-full" /></div>;
  if (!driver) return <div className="p-6 text-muted-foreground">Conducteur introuvable</div>;

  const alert = licenseAlert(driver.licenseExpiry);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/drivers"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold">{driver.firstName} {driver.lastName}</h1>
        <Badge variant={DRIVER_STATUS_VARIANT[driver.status]}>{DRIVER_STATUS_LABELS[driver.status]}</Badge>
        {canWrite && (
          <div className="ml-auto w-52">
            <Select value={driver.status} onValueChange={(v) => setStatus.mutate(v as DriverStatus)} disabled={setStatus.isPending}>
              <SelectTrigger aria-label="Changer le statut"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(DRIVER_STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="missions">Missions ({missions?.total ?? 0})</TabsTrigger>
          <TabsTrigger value="app">App chauffeur</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Fiche conducteur</CardTitle>
              {!editing && canWrite && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="h-4 w-4 mr-1" /> Modifier
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {editing ? (
                <DriverForm
                  initial={driver}
                  submitLabel="Enregistrer"
                  pending={update.isPending}
                  onSubmit={(d) => update.mutate(d)}
                  onCancel={() => setEditing(false)}
                />
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Field label="Prénom" value={driver.firstName} />
                  <Field label="Nom" value={driver.lastName} />
                  <Field label="N° de permis" value={driver.licenseNumber} />
                  <div>
                    <Field label="Expiration du permis" value={fmtDay(driver.licenseExpiry)} />
                    {alert && (
                      <Badge variant={alert === 'expired' ? 'destructive' : 'warning'} className="mt-1">
                        {alert === 'expired' ? 'Permis expiré' : 'Expire dans moins de 30 jours'}
                      </Badge>
                    )}
                  </div>
                  <Field label="Téléphone" value={driver.phone} />
                  <Field label="Email" value={driver.email} />
                  <Field label="Date d’embauche" value={fmtDay(driver.hireDate)} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="missions">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Référence</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Trajet</TableHead>
                    <TableHead>Date prévue</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missions?.data.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono font-semibold">
                        <Link to="/missions/$id" params={{ id: m.id }} className="underline">{m.reference}</Link>
                      </TableCell>
                      <TableCell>{m.client?.name ?? '—'}</TableCell>
                      <TableCell>{m.originSite?.name ?? '—'} → {m.destSite?.name ?? '—'}</TableCell>
                      <TableCell>{fmtDay(m.scheduledAt)}</TableCell>
                      <TableCell><Badge variant="outline">{m.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {missions && missions.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Aucune mission affectée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="app">
          <Card>
            <CardHeader><CardTitle>Accès à l’app chauffeur</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              {driver.userId ? (
                <p className="inline-flex items-center gap-2 text-success">
                  <Smartphone className="h-4 w-4" /> Ce conducteur a un compte : il se connecte à l’app chauffeur avec son email.
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Ce conducteur n’a pas encore de compte. Générez un QR code et faites-le scanner depuis son téléphone
                  pour qu’il crée son accès à l’app chauffeur.
                </p>
              )}
              {canWrite && (
                <Button variant="outline" onClick={() => genOnboarding.mutate()} disabled={genOnboarding.isPending}>
                  <QrCode className="h-4 w-4 mr-2" /> {onboarding ? 'Générer un nouveau QR code' : 'Générer un QR code d’activation'}
                </Button>
              )}
              {onboarding && (
                <div className="flex items-start gap-6">
                  <img src={onboarding.qrDataUrl} alt="QR code d’activation" className="w-48 h-48 border rounded" />
                  <div className="space-y-2">
                    <p>Valable jusqu’au <strong>{new Date(onboarding.expiresAt).toLocaleString('fr-FR')}</strong>.</p>
                    <p className="text-xs text-muted-foreground break-all">{onboarding.url}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs uppercase tracking-wide">{label}</p>
      <p className="font-medium mt-0.5">{value || '—'}</p>
    </div>
  );
}
